import { Helmet } from "react-helmet-async";
import { useSettings } from "../contexts/SettingsContext";
import { resolveAssetDisplayUrl } from "../lib/assetUrl";

const SITE_NAME = "Lòng Vòng Đà Lạt";
const SITE_URL = "https://longvongdalat.com";
const DEFAULT_IMAGE = "https://cdn.longvongdalat.com/og-image.png";
const DEFAULT_DESC =
  "Lòng Vòng Đà Lạt – hướng dẫn du lịch thành phố sương mù: địa điểm nổi tiếng, ăn uống, homestay và bản đồ tương tác.";

/**
 * SEO component — đặt trong từng page để override title/meta.
 *
 * Props:
 *  title        string  — tiêu đề page (sẽ được nối " | Đà Lạt Tour")
 *  description  string  — mô tả 150-160 ký tự
 *  image        string  — URL ảnh OG (tuyệt đối)
 *  url          string  — canonical URL đầy đủ (mặc định = SITE_URL + pathname)
 *  type         string  — og:type (mặc định "website")
 *  noindex      bool    — true → noindex (trang admin, private)
 *  jsonLd       object|array — JSON-LD structured data
 */
export default function SEO({
  title,
  description = DEFAULT_DESC,
  image,
  url,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  const { settings } = useSettings();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} – Khám phá địa điểm, ẩm thực & văn hoá Đà Lạt`;
  const canonical = url || (typeof window !== "undefined" ? `${SITE_URL}${window.location.pathname}` : SITE_URL);
  const settingsOgImage = resolveAssetDisplayUrl(settings?.ogImage?.url, settings?.ogImage?.key);
  const ogImage = image || settingsOgImage || DEFAULT_IMAGE;

  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="vi_VN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
